/**
 * Générateur de clés de cache pour les endpoints d'API
 * Utilitaire réutilisable pour créer des clés de cache cohérentes
 */

/**
 * Génère un nom d'affichage à partir de la route
 * @param {string} route - La route de l'API
 * @returns {string} Nom d'affichage calculé
 */
export function getDisplayNameFromRoute(route) {
  // Extraire la dernière partie de la route après /api/
  const parts = route.replace('/api/', '').split('/');
  const lastPart = parts[0] || parts[parts.length - 1];
  
  // Capitaliser et formatter
  return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
}

/**
 * Génère une clé de cache pour un endpoint avec ses paramètres
 * @param {string} endpoint - L'endpoint de l'API
 * @param {Object} options - Options de la requête (pour inclure les query params)
 * @returns {string} Clé de cache
 */
export function generateCacheKey(endpoint, options = {}) {
  // Extraire les query params de l'endpoint s'ils existent
  const url = new URL(endpoint, 'http://localhost');
  const searchParams = url.searchParams.toString();
  
  // Supprimer le préfixe commun /api/ pour des clés plus courtes
  let cacheKey = url.pathname.replace(/^\/api\//, '');
  
  // Si la clé est vide après suppression du préfixe, utiliser 'root'
  if (!cacheKey || cacheKey === '/') {
    cacheKey = 'root';
  }
  
  if (searchParams) {
    cacheKey += `?${searchParams}`;
  }
  
  return cacheKey;
}

/**
 * Génère une clé de cache à partir d'une requête HTTP
 * @param {Request} request - Objet Request
 * @returns {string} Clé de cache
 */
export function generateCacheKeyFromRequest(request) {
  const url = new URL(request.url);
  const searchParams = url.searchParams.toString();
  
  // Supprimer le préfixe commun /api/ pour des clés plus courtes
  let cacheKey = url.pathname.replace(/^\/api\//, '');
  
  // Si la clé est vide après suppression du préfixe, utiliser 'root'
  if (!cacheKey || cacheKey === '/') {
    cacheKey = 'root';
  }
  
  if (searchParams) {
    cacheKey += `?${searchParams}`;
  }
  
  return cacheKey;
}

/**
 * Génère une clé de cache personnalisée avec préfixe
 * @param {string} prefix - Préfixe pour la clé
 * @param {Object} params - Paramètres à inclure dans la clé
 * @returns {string} Clé de cache
 */
export function generateCustomCacheKey(prefix, params = {}) {
  let cacheKey = prefix;
  
  const paramEntries = Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .sort(([a], [b]) => a.localeCompare(b)); // Tri pour cohérence
  
  if (paramEntries.length > 0) {
    const paramString = paramEntries
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    cacheKey += `?${paramString}`;
  }
  
  return cacheKey;
}
